import { getAllIds, getCountByStatus, useGetAllAuthors } from "@/api/author/author";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewAuthorDialog from "@/components/autor/NewAuthorDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  authorsAdvancedSearchConfig,
  authorsColumns,
} from "@/config/dashboard/authors";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Author } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "author-table-preferences";

const flatVisibleAuthorsColumns = [
  ...authorsColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const AuthorDashboard = () => {
  const { t } = useTranslation();

  const { currentCount, fetchParams, pagination, setPagination } =
    useDashboardParams<Author>({
      resourceName: "author",
      resourceCounter: getCountByStatus,
      statusField: "artistesAuteursPublicationStatus",
    });

  const { data } = useGetAllAuthors(fetchParams);

  const navigate = useNavigate();

  const actionsColumn = useDashboardActionsColumn<Author>({
    documentType: "author",
    publicationStatusField: "artistesAuteursPublicationStatus",
    onEdit: (row) => navigate(`/edit/author/${row.id}`),
  });

  const columns: ColumnDef<Author, any>[] = useMemo(() => {
    return [...generateTable(authorsColumns, "author"), actionsColumn];
  }, [actionsColumn]);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return ensureActionsColumnLast(order || flatVisibleAuthorsColumns);
    }
    return flatVisibleAuthorsColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...authorsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...authorsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
        [ACTIONS_COLUMN_ID]: true,
      };
    },
  );

  const { selectionState, setSelectionState, dataTableSelection } =
    useDashboardRowSelection<Author>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitleArtistes")}</p>
        <GlobalSearch resource="authors" config={authorsAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row items-end justify-end w-full">
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={authorsAdvancedSearchConfig}
            resource="authors"
          />
          <AdvancedSort config={authorsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createAauthor")}
            Dialog={NewAuthorDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/author/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
          }}
          selection={dataTableSelection}
        />
        <DashboardSelectionBar
          documentType="author"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        />
      </div>
    </div>
  );
};

export default AuthorDashboard;
